import type {
  ThemeActionButtons,
  ThemeStatusBadges,
  ThemeProgressBar,
  ThemeChartColors,
  ThemeIconAccents,
  ThemeStateStyles,
} from '@shared/types';

export interface ThemeConfig {
  name: string;
  colors: {
    primary: string; // Main theme color
    primaryDark: string; // Darker variant
    secondary: string; // Secondary accent
    accent: string; // Highlight color
    background: string; // Background color
    backgroundLight: string; // Light background variant
    text: string; // Primary text color
    textMuted: string; // Muted text color
    border: string; // Border color
  };
  card: {
    bg: string; // Card background
    bgHover: string; // Card hover background
    border: string; // Card border
    shadow: string; // Card shadow
  };
  button: {
    bg: string; // Button background
    bgHover: string; // Button hover background
    text: string; // Button text
    textHover: string; // Button hover text
    shadow: string; // Button shadow
  };
  decorative: {
    bg: string; // Decorative element background
    opacity: string; // Decorative element opacity
  };
  // Extended theme fields (optional, with defaults applied at runtime)
  actionButtons?: ThemeActionButtons;
  statusBadges?: ThemeStatusBadges;
  progressBar?: ThemeProgressBar;
  chartColors?: ThemeChartColors;
  iconAccents?: ThemeIconAccents;
  stateStyles?: ThemeStateStyles;
}

export const exerciseThemes: Record<string, ThemeConfig> = {
  // Sage - Green success theme (kept for legacy)
  sage: {
    name: 'Sage',
    colors: {
      primary: 'sage',
      primaryDark: 'sage-dark',
      secondary: 'sage-dark',
      accent: 'sage-dark',
      background: 'sand',
      backgroundLight: 'sage/10',
      text: 'text-sage-dark',
      textMuted: 'text-sage-dark/80',
      border: 'sage/20',
    },
    card: {
      bg: 'bg-sage/10',
      bgHover: 'hover:bg-sage/15',
      border: 'border-sage/20',
      shadow: 'shadow-sage/20',
    },
    button: {
      bg: 'bg-sage',
      bgHover: 'hover:bg-sage-dark',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-sage/30',
    },
    decorative: {
      bg: 'bg-sage/20',
      opacity: 'opacity-60',
    },
  },

  // Foliage - Gold accent theme
  foliage: {
    name: 'Foliage',
    colors: {
      primary: 'foliage',
      primaryDark: 'foliage',
      secondary: 'growth',
      accent: 'foliage',
      background: 'sand',
      backgroundLight: 'foliage/10',
      text: 'text-growth',
      textMuted: 'text-growth/70',
      border: 'foliage/20',
    },
    card: {
      bg: 'bg-foliage/10',
      bgHover: 'hover:bg-foliage/15',
      border: 'border-foliage/20',
      shadow: 'shadow-foliage/20',
    },
    button: {
      bg: 'bg-foliage',
      bgHover: 'hover:opacity-90',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-foliage/30',
    },
    decorative: {
      bg: 'bg-foliage/20',
      opacity: 'opacity-60',
    },
  },

  // Growth - Primary dark green theme (was ocean)
  growth: {
    name: 'Growth',
    colors: {
      primary: 'growth',
      primaryDark: 'growth',
      secondary: 'growth',
      accent: 'growth',
      background: 'sand',
      backgroundLight: 'growth/10',
      text: 'text-growth',
      textMuted: 'text-growth/70',
      border: 'growth/20',
    },
    card: {
      bg: 'bg-growth/10',
      bgHover: 'hover:bg-growth/15',
      border: 'border-growth/20',
      shadow: 'shadow-growth/20',
    },
    button: {
      bg: 'bg-growth',
      bgHover: 'hover:opacity-90',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-growth/30',
    },
    decorative: {
      bg: 'bg-growth/20',
      opacity: 'opacity-60',
    },
  },

  // Ocean - Legacy theme using ocean color alias
  ocean: {
    name: 'Ocean',
    colors: {
      primary: 'ocean',
      primaryDark: 'ocean-dark',
      secondary: 'ocean',
      accent: 'ocean',
      background: 'sand',
      backgroundLight: 'ocean/10',
      text: 'text-ocean',
      textMuted: 'text-ocean/70',
      border: 'ocean/20',
    },
    card: {
      bg: 'bg-ocean/10',
      bgHover: 'hover:bg-ocean/15',
      border: 'border-ocean/20',
      shadow: 'shadow-ocean/20',
    },
    button: {
      bg: 'bg-ocean',
      bgHover: 'hover:bg-ocean-dark',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-ocean/30',
    },
    decorative: {
      bg: 'bg-ocean/20',
      opacity: 'opacity-60',
    },
  },

  // Clay - Warm orange accent theme
  clay: {
    name: 'Clay',
    colors: {
      primary: 'clay',
      primaryDark: 'clay-dark',
      secondary: 'clay-dark',
      accent: 'clay-dark',
      background: 'sand',
      backgroundLight: 'clay/10',
      text: 'text-clay-dark',
      textMuted: 'text-clay-dark/70',
      border: 'clay/20',
    },
    card: {
      bg: 'bg-clay/10',
      bgHover: 'hover:bg-clay/15',
      border: 'border-clay/20',
      shadow: 'shadow-clay/20',
    },
    button: {
      bg: 'bg-clay',
      bgHover: 'hover:bg-clay-dark',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-clay/30',
    },
    decorative: {
      bg: 'bg-clay/20',
      opacity: 'opacity-60',
    },
  },

  // Sky - Blue accent theme
  sky: {
    name: 'Sky',
    colors: {
      primary: 'sky',
      primaryDark: 'sky',
      secondary: 'growth',
      accent: 'sky',
      background: 'sand',
      backgroundLight: 'sky/10',
      text: 'text-growth',
      textMuted: 'text-growth/70',
      border: 'sky/20',
    },
    card: {
      bg: 'bg-sky/10',
      bgHover: 'hover:bg-sky/15',
      border: 'border-sky/20',
      shadow: 'shadow-sky/20',
    },
    button: {
      bg: 'bg-sky',
      bgHover: 'hover:opacity-90',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-sky/30',
    },
    decorative: {
      bg: 'bg-sky/20',
      opacity: 'opacity-60',
    },
  },

  // Branch - Warm cream theme
  branch: {
    name: 'Branch',
    colors: {
      primary: 'branch',
      primaryDark: 'foliage',
      secondary: 'growth',
      accent: 'foliage',
      background: 'sand',
      backgroundLight: 'branch/50',
      text: 'text-growth',
      textMuted: 'text-growth/70',
      border: 'foliage/20',
    },
    card: {
      bg: 'bg-branch/50',
      bgHover: 'hover:bg-branch/70',
      border: 'border-foliage/20',
      shadow: 'shadow-foliage/20',
    },
    button: {
      bg: 'bg-growth',
      bgHover: 'hover:opacity-90',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-growth/30',
    },
    decorative: {
      bg: 'bg-foliage/20',
      opacity: 'opacity-60',
    },
  },

  // Stream - Light blue theme
  stream: {
    name: 'Stream',
    colors: {
      primary: 'sky',
      primaryDark: 'sky',
      secondary: 'growth',
      accent: 'sky',
      background: 'sand',
      backgroundLight: 'stream',
      text: 'text-growth',
      textMuted: 'text-growth/70',
      border: 'sky/20',
    },
    card: {
      bg: 'bg-stream',
      bgHover: 'hover:bg-sky/20',
      border: 'border-sky/20',
      shadow: 'shadow-sky/20',
    },
    button: {
      bg: 'bg-sky',
      bgHover: 'hover:opacity-90',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-sky/30',
    },
    decorative: {
      bg: 'bg-sky/20',
      opacity: 'opacity-60',
    },
  },

  // Legacy themes kept for backwards compatibility
  sunset: {
    name: 'Sunset',
    colors: {
      primary: 'clay',
      primaryDark: 'clay',
      secondary: 'foliage',
      accent: 'clay',
      background: 'sand',
      backgroundLight: 'clay/10',
      text: 'text-growth',
      textMuted: 'text-growth/70',
      border: 'clay/20',
    },
    card: {
      bg: 'bg-clay/10',
      bgHover: 'hover:bg-clay/15',
      border: 'border-clay/20',
      shadow: 'shadow-clay/20',
    },
    button: {
      bg: 'bg-clay',
      bgHover: 'hover:opacity-90',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-clay/30',
    },
    decorative: {
      bg: 'bg-clay/20',
      opacity: 'opacity-60',
    },
  },

  forest: {
    name: 'Forest',
    colors: {
      primary: 'growth',
      primaryDark: 'growth',
      secondary: 'sage',
      accent: 'growth',
      background: 'sand',
      backgroundLight: 'sage/10',
      text: 'text-growth',
      textMuted: 'text-growth/70',
      border: 'sage/20',
    },
    card: {
      bg: 'bg-sage/10',
      bgHover: 'hover:bg-sage/15',
      border: 'border-sage/20',
      shadow: 'shadow-sage/20',
    },
    button: {
      bg: 'bg-growth',
      bgHover: 'hover:opacity-90',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-growth/30',
    },
    decorative: {
      bg: 'bg-sage/20',
      opacity: 'opacity-60',
    },
  },

  lavender: {
    name: 'Lavender',
    colors: {
      primary: 'sky',
      primaryDark: 'sky',
      secondary: 'sky',
      accent: 'sky',
      background: 'sand',
      backgroundLight: 'sky/10',
      text: 'text-growth',
      textMuted: 'text-growth/70',
      border: 'sky/20',
    },
    card: {
      bg: 'bg-sky/10',
      bgHover: 'hover:bg-sky/15',
      border: 'border-sky/20',
      shadow: 'shadow-sky/20',
    },
    button: {
      bg: 'bg-sky',
      bgHover: 'hover:opacity-90',
      text: 'text-white',
      textHover: 'hover:text-white',
      shadow: 'shadow-sky/30',
    },
    decorative: {
      bg: 'bg-sky/20',
      opacity: 'opacity-60',
    },
  },
};

/**
 * Theme for completed exercises - diminished, muted styling
 */
export const completedTheme: ThemeConfig = {
  name: 'Completed',
  colors: {
    primary: 'stone',
    primaryDark: 'stone',
    secondary: 'stone',
    accent: 'stone',
    background: 'sand',
    backgroundLight: 'stone/10',
    text: 'text-growth/50',
    textMuted: 'text-growth/40',
    border: 'stone/30',
  },
  card: {
    bg: 'bg-stone/20',
    bgHover: 'hover:bg-stone/25',
    border: 'border-stone/30',
    shadow: 'shadow-none',
  },
  button: {
    bg: 'bg-stone',
    bgHover: 'hover:opacity-90',
    text: 'text-white',
    textHover: 'hover:text-white',
    shadow: 'shadow-none',
  },
  decorative: {
    bg: 'bg-stone/20',
    opacity: 'opacity-30',
  },
};

/**
 * Get theme configuration by theme name
 */
export const getTheme = (themeName: string): ThemeConfig => {
  return exerciseThemes[themeName] || exerciseThemes.sage;
};
